import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type {
  Account,
  Asset,
  AssetMovement,
  AssetValueReport,
  Attachment,
  AuditEvent,
  AuthStatus,
  Budget,
  Category,
  ChoreAnalytics,
  ChoreCategory,
  ChoreOccurrence,
  ChoreTemplate,
  ChoreTimelineEntry,
  ChoreView,
  EntityReferenceId,
  FinanceOverview,
  InsuranceCoverageReport,
  AuthIdentity,
  HouseholdMember,
  HouseholdMembership,
  Location,
  MealAssemblerResponse,
  MealPlanEntry,
  Product,
  Recipe,
  RecurringItem,
  ScanData,
  ShoppingList,
  ShoppingTrip,
  StockLot,
  Store,
  Summary,
  TaskOccurrence,
  Transaction,
  User
} from "../app/types.js";
import type { AttachmentCollectionType } from "@household/shared/attachments";
import type { EntityRouteTarget } from "../app/entityRoutes.js";
import type { DashboardSection } from "../app/loaders.js";
import type { Tab } from "./shell/navigation.js";

export type RefreshScope =
  | "active"
  | "dashboard"
  | "search"
  | "inventory"
  | "assets"
  | "groceries"
  | "chores"
  | "projects"
  | "floor-plan"
  | "bms"
  | "network"
  | "recipes"
  | "meal-assembler"
  | "shopping"
  | "feedback"
  | "finance"
  | "settings";

export type ToastAction = { label: string; onClick: () => void | Promise<void> };

export type RunOptions = { refresh?: RefreshScope | false; successMessage?: string };

/**
 * One write by a surface that renders its own result — see `beginLocalWrite`.
 * Exactly one of `landed` / `abandon` is called, once.
 */
export type LocalWriteCycle = { landed: () => void; abandon: () => void };

/**
 * Deep link on the toast's first row (#586) — "the thing I just changed is over
 * there". Structurally an `EntityRouteTarget`, so `routeForEntity(...)` output
 * drops straight in; `label` is the link text, so callers override the
 * entity-type wording ("shopping item") with something readable ("BJs
 * Shopping"). Distinct from `ToastAction`: a link navigates, an action mutates.
 */
export type ToastLink = EntityRouteTarget;

export type ToastOptions = { actions?: ToastAction[]; link?: ToastLink };

export type ToastMessage = { id: number; message: string; actions?: ToastAction[]; link?: ToastLink };

export type HouseholdContextValue = {
  tab: Tab;
  setTab: (tab: Tab) => void;
  /**
   * This instance is the public read-only demo (#839). Shell and the command
   * palette already took it as a prop; site search needs it too, because it
   * lists the app's own pages (#1037) and must hide exactly the tabs the
   * sidebar hides rather than becoming the way around that.
   */
  demoMode?: boolean;
  identity: AuthIdentity;
  user: User;
  households: HouseholdMembership[];
  switchHousehold: (householdId: string) => Promise<void>;
  /**
   * Push a fresh auth payload into app state (#1057). The account endpoints
   * answer with the whole payload, so a rename lands in the header eyebrow and
   * everywhere else in one round trip instead of a reload.
   */
  applyAuthStatus: (status: AuthStatus) => void;
  /** Clear client state after signing out — the same handler the header uses. */
  onSignedOut: () => void;
  error: string;
  setError: (message: string) => void;
  toast: ToastMessage | null;
  // The bare-array form is the original signature and still the common case;
  // pass the object form to attach a first-row deep link (#586).
  showToast: (message: string, options?: ToastAction[] | ToastOptions) => void;
  dismissToast: () => void;
  busy: boolean;
  summary: Summary | null;
  /**
   * The dashboard reads that failed on the last load (September audit). A
   * panel whose section is listed shows "couldn't load" in place of its empty
   * copy; the section's data keeps its previous value meanwhile.
   */
  dashboardLoadFailures: DashboardSection[];
  /** What the household's things are worth, by category (#1187). */
  assetValue: AssetValueReport | null;
  /** What the policies say they cover, against those holdings (#1211). Null
   * until the dashboard load answers; empty counts once it has. */
  insuranceCoverage: InsuranceCoverageReport | null;
  locations: Location[];
  assets: Asset[];
  assetQuery: string;
  assetRouteId: EntityReferenceId | null;
  assetNotFound: boolean;
  assetLoadError: string | null;
  retryAssetLoad: () => void;
  // #980: the place whose detail page is open, from /inventory/places/{id}.
  placeRouteId: EntityReferenceId | null;
  /** The open food page (#1045). Null on the Food tab proper. */
  productRouteId: EntityReferenceId | null;
  openFoodDetails: (productId: EntityReferenceId, options?: { replace?: boolean }) => void;
  closeFoodDetails: (options?: { replace?: boolean }) => void;
  recipeRouteId: EntityReferenceId | null;
  setAssetQuery: (query: string) => void;
  products: Product[];
  stockLots: StockLot[];
  expirations: StockLot[];
  recipes: Recipe[];
  mealPlanEntries: MealPlanEntry[];
  shoppingLists: ShoppingList[];
  shoppingTrips: ShoppingTrip[];
  /**
   * How many COMPLETE shopping payloads (lists + trips + stores) have landed for
   * this household, and whether one is in flight right now (#1311). 0 means
   * empty `shoppingLists`/`shoppingTrips` are "still loading", not "none" —
   * which is what lets a `?listId=`/`?tripId=` deep link wait for its record.
   *
   * Only `loadShopping` moves these. The dashboard and recipes payloads also
   * carry `shoppingLists`, but without trips or stores, so they are not a
   * shopping payload for this purpose. Compare the version against the one
   * observed when a route was requested: "loaded once" is not "loaded since you
   * asked", and a deep link to a list another member just created must wait for
   * the refresh that is already on its way rather than be rejected against the
   * previous payload.
   */
  shoppingPayloadVersion: number;
  /**
   * Shopping fetches that failed with something a retry will not fix (a 5xx, an
   * expired session); transient network failures are not counted, because the
   * offline cache and reconnecting already answer for those. A route waiting for
   * a payload compares this against the value it saw when it asked, so a failure
   * that happened after the request ends the wait — including a failure of the
   * shell's own tab refresh, which the route never made itself.
   */
  shoppingPayloadFailures: number;
  shoppingRefreshing: boolean;
  stores: Store[];
  accounts: Account[];
  categories: Category[];
  transactions: Transaction[];
  budgets: Budget[];
  recurringItems: RecurringItem[];
  financeOverview: FinanceOverview | null;
  audit: AuditEvent[];
  taskOccurrences: TaskOccurrence[];
  choreView: ChoreView;
  setChoreView: (view: ChoreView) => void;
  choreOccurrences: ChoreOccurrence[];
  choreCategories: ChoreCategory[];
  choreTemplates: ChoreTemplate[];
  choreAnalytics: ChoreAnalytics | null;
  choreUsers: HouseholdMember[];
  choreTimeline: ChoreTimelineEntry[];
  selectedChore: ChoreOccurrence | null;
  setSelectedChore: (chore: ChoreOccurrence | null) => void;
  selectedAsset: Asset | null;
  setSelectedAsset: (asset: Asset | null) => void;
  assetMovements: AssetMovement[];
  assetAttachments: Attachment[];
  scanToken: string;
  scanData: ScanData | null;
  /** Why the scanned code did not resolve (September audit): "not-found", or the failure's message. */
  scanLoadError: string | null;
  setScanData: (scanData: ScanData | null) => void;
  // Kitchen signals (ready-now / use-soon) for the Meals hub's dinner-tonight
  // page and the settings-page assembler config; deeper meal suggestions moved
  // to the AI assistant's tools (#589/#615).
  mealData: MealAssemblerResponse | null;
  // The lists food may be added to, newest-updated first, and the default
  // target among them. Project materials lists are excluded from both (#465
  // kind); `shoppingLists` still holds every list for browsing.
  groceryLists: ShoppingList[];
  activeList: ShoppingList | undefined;
  run: (action: () => Promise<void>, options?: RunOptions) => Promise<boolean>;
  refreshActiveTab: (scope?: RefreshScope) => Promise<void>;
  /**
   * Refresh a scope after a write this surface already performed itself. Same
   * as `refreshActiveTab`, but it also tells the realtime handler that this
   * change is already accounted for, so the SSE echo of the same write does not
   * trigger a second full refresh (#520). Use `refreshActiveTab` for refreshes
   * that are not the direct consequence of a write.
   */
  refreshAfterOwnChange: (scope: RefreshScope) => Promise<void>;
  /**
   * For a surface that writes and ALREADY SHOWS the result without refetching —
   * the floor-plan editor, which holds the authoritative document in React
   * state and PATCHes it out (#1031).
   *
   * `refreshAfterOwnChange` is the wrong tool for those: it earns its echo
   * suppression by performing a refetch, and the refetch is the harm here — it
   * rebuilds `detail` and remounts the editor, throwing away zoom, selection,
   * tool and undo history. Left alone, the surface's own write comes back over
   * SSE as an ordinary uncovered write and forces exactly that refresh about a
   * second after every edit.
   *
   * Open one of these when the write goes out, call `landed()` when it lands —
   * the local view is the fresh data, so that stands in for the refresh — and
   * `abandon()` if it did not, which puts the echo back to being worth acting
   * on and lets the screen recover.
   */
  beginLocalWrite: (scope: RefreshScope) => LocalWriteCycle;
  suppressRealtimeBanner: (durationMs?: number) => void;
  loadDashboard: () => Promise<void>;
  loadInventory: () => Promise<void>;
  loadAssets: () => Promise<void>;
  loadGroceries: () => Promise<void>;
  loadChores: () => Promise<void>;
  loadRecipes: (options?: { catalog?: boolean }) => Promise<void>;
  loadMealAssembler: (signal?: AbortSignal) => Promise<void>;
  loadShopping: () => Promise<void>;
  // Trip-scoped state patch for the optimistic in-store check-off (#702): swap
  // one trip in place instead of reloading the whole shopping catalog per tap.
  patchShoppingTrip: (tripId: string, update: (trip: ShoppingTrip) => ShoppingTrip) => void;
  loadFinance: () => Promise<void>;
  reloadScanData: (token?: string) => Promise<void>;
  completeTaskOccurrence: (occurrence: TaskOccurrence) => Promise<void>;
  completeTaskOccurrences: (occurrences: TaskOccurrence[]) => Promise<void>;
  snoozeTaskOccurrence: (occurrence: TaskOccurrence) => Promise<void>;
  snoozeTaskOccurrences: (occurrences: TaskOccurrence[]) => Promise<void>;
  catchUpTaskOccurrences: (occurrences: TaskOccurrence[]) => Promise<void>;
  skipTaskOccurrences: (occurrences: TaskOccurrence[]) => Promise<void>;
  createChore: (form: HTMLFormElement) => Promise<EntityReferenceId | null>;
  uploadChoreAttachments: (taskId: EntityReferenceId, files: File[]) => void;
  /** Detached asset upload (#733): saves first, uploads after, reorders in the
   * background so the cover the user picked survives. */
  uploadAssetAttachments: (input: {
    assetId: EntityReferenceId;
    files: File[];
    primaryPhotoIndex?: number | null;
    existingPhotoIds?: number[];
    fallbackPrimaryAttachmentId?: number | null;
    label: string;
    /** What these files ARE (#1176); defaults to the photo kind. */
    collectionType?: AttachmentCollectionType;
    onUploaded?: (result: { photoIds: number[]; primaryAttachmentId: number | null }) => void;
  }) => void;
  updateChore: (chore: ChoreOccurrence, form: HTMLFormElement) => Promise<void>;
  deleteChore: (chore: ChoreOccurrence) => Promise<void>;
  completeChoreOccurrence: (occurrence: ChoreOccurrence) => Promise<void>;
  snoozeChoreOccurrence: (occurrence: ChoreOccurrence) => Promise<void>;
  skipChoreOccurrence: (occurrence: ChoreOccurrence) => Promise<void>;
  reopenChoreOccurrence: (occurrence: ChoreOccurrence) => Promise<void>;
  applyChoreTemplate: (template: ChoreTemplate) => Promise<void>;
  openChoreTimeline: (chore: ChoreOccurrence) => Promise<void>;
  handleScanToken: (token: string) => void;
  closeAssetDetails: () => void;
  openAssetDetails: (asset: Asset, options?: { navigate?: boolean; replace?: boolean }) => Promise<void>;
  openPlaceDetails: (placeId: EntityReferenceId, options?: { replace?: boolean }) => void;
  closePlaceDetails: (options?: { replace?: boolean }) => void;
  openRecipeDetails: (recipeId: EntityReferenceId) => void;
  closeRecipeDetails: () => void;
  recipeName: (recipeId?: EntityReferenceId | null) => string;
  locationName: (locationId?: EntityReferenceId | null) => string;
  accountName: (accountId?: EntityReferenceId | null) => string;
};

const HouseholdContext = createContext<HouseholdContextValue | null>(null);

export function HouseholdProvider({ value, children }: { value: HouseholdContextValue; children: ReactNode }) {
  return <HouseholdContext.Provider value={value}>{children}</HouseholdContext.Provider>;
}

export function useHousehold() {
  const context = useContext(HouseholdContext);
  if (!context) throw new Error("useHousehold must be used inside HouseholdProvider");
  return context;
}
